import { createTheme } from '@algoan/utils/theme';

export interface Theme {
  /**
   * Font family for the project
   */
  fontFamily: string;
  /**
   * Digital text
   */
  middleGrey: string;
  darkGrey: string;
  algoanBlue: string;
  backgroundBlue: string;
  bluePeps: string;
  blueHelp: string;
  selectedBlue: string;
  selectedIconBgBlue: string;
  delete: string;
  redNotification: string;
  backgroundGrey: string;
  lightBlue: string;
  sweetGrey: string;
  strongGrey: string;
  accessibleGrey: string;
  iceBlue: string;
  white: string;
  backgroundMenu: string;
  teal: string;
  softBlue: string;
  seaGreen: string;
  orangeWarning: string;
  softPumpkin: string;
  skyBlue: string;
  seaBlue: string;
  pistachioGreen: string;
  appleGreen: string;
  softGrey: string;
  blueGrey: string;
  silverGrey: string;
  sideWalkGrey: string;
  coldGrey: string;
  beauBlue: string;
  magento: string;
  lightYellow: string;
  deepYellow: string;
  fiji: string;
  coastalBreeze: string;
  paleBlue: string;
  placeholderColors: {
    primary: string;
    primaryHover: string;
    primaryTransparent: string;
  };
  scoreIndicatorColors: {
    positiveValue: string;
    negativeValue: string;
    neutralValue: string;
    emptyValue: string;
  };
  /**
   * Colors used for Notification
   */
  feedbackColors: {
    /**
     * Notification background - success
     */
    softGreen: string;
    /**
     * Validation
     */
    greenNotification: string;
    /**
     * Notification background - error
     */
    softRed: string;
    /**
     * Error
     */
    deepRed: string;
    deepOrange: string;
  };
  fontWeight: {
    light: number;
    regular: number;
    medium: number;
    semiBold: number;
    bold: number;
    extraBold: number;
  };
  breakpoints: {
    mobile: string;
    mobileLarge: string;
    tablet: string;
    laptopSmall: string;
    laptop: string;
    screen4k: string;
  };
}

export const theme: Theme = {
  fontFamily: `'Euclid Circular B', sans-serif`,
  middleGrey: '#798E9F',
  darkGrey: '#4E7390',
  algoanBlue: '#00466B',
  backgroundBlue: '#005A9E',
  bluePeps: '#0B7ACE',
  blueHelp: '#51A0DC',
  selectedBlue: '#DFF1FF',
  selectedIconBgBlue: '#C8E4F9',
  delete: '#E7281A',
  redNotification: '#D31111',
  lightBlue: '#98BCD7',
  softGrey: '#B9C6CF',
  blueGrey: '#60707C',
  silverGrey: '#47535C',
  sideWalkGrey: '#7C8D9A',
  beauBlue: '#C2D2DA',
  paleBlue: '#f6fbff',
  lightYellow: '#FEF9E1',
  deepYellow: '#C49105',
  sweetGrey: '#ECF0F3',
  strongGrey: '#4E7391',
  coldGrey: '#DEE3E6',
  accessibleGrey: '#385872',
  iceBlue: '#F0F4F7',
  white: '#FFFFFF',
  backgroundMenu: '#FBFBFC',
  backgroundGrey: '#F8F9FA',
  teal: '#20BEB5',
  softBlue: '#F3F8FD',
  seaGreen: '#76CF9A',
  orangeWarning: '#F46F0E',
  softPumpkin: '#FFF0E1',
  skyBlue: '#E5F9FF',
  seaBlue: '#0B9FCE',
  pistachioGreen: '#E5FFE1',
  appleGreen: '#44A035',
  magento: '#C242FF',
  fiji: '#05ACB4',
  coastalBreeze: '#E1F6F7',
  scoreIndicatorColors: {
    positiveValue: '#0BDC9D',
    negativeValue: '#FF6348',
    neutralValue: '#4BAAFF',
    emptyValue: '#CDDBE2',
  },
  placeholderColors: {
    primary: '#B292F5',
    primaryHover: '#8E75C4',
    primaryTransparent: '#B292F5',
  },
  feedbackColors: {
    softGreen: '#DEFFF3',
    greenNotification: '#12B17A',
    softRed: '#FFE4E4',
    deepRed: '#C34036',
    deepOrange: '#FF7426',
  },
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
  },
  breakpoints: {
    mobile: '375px',
    mobileLarge: '425px',
    tablet: '768px',
    laptopSmall: '1024px',
    laptop: '1440px',
    screen4k: '2560px',
  },
};

export const addAlpha = (color: string, opacity: number): string => {
  const calculatedOpacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
  return color + calculatedOpacity.toString(16).toUpperCase().padStart(2, '0');
};

export const { styled, createGlobalStyle, keyframes, useTheme, css } = createTheme<Theme>();
